import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../../api/apiClient";

const MIN_QUERY = 3;
const DEBOUNCE_MS = 300;

/**
 * "Search DYC first" lookup for the supply-chain editor.
 *   mode "public" -> GET /partner-search (registration, no account yet)
 *   mode "auth"   -> GET /supply-chain/license-search (signed in; excludes excludeLicenseNo)
 * Results: [{ id, company, city, state, profile }] — `id` is the opaque license id sent back
 * as linkedLicenseId. Stale responses are dropped (AbortController).
 */
export default function useLicenseSearch({ mode = "auth", excludeLicenseNo = null } = {}) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [status, setStatus] = useState("idle"); // idle | searching | done | error
    const timer = useRef(null);
    const controller = useRef(null);

    useEffect(
        () => () => {
            clearTimeout(timer.current);
            controller.current?.abort();
        },
        []
    );

    const run = useCallback(
        async (q) => {
            controller.current?.abort();
            if (q.trim().length < MIN_QUERY) {
                setResults([]);
                setStatus("idle");
                return;
            }
            const ctrl = new AbortController();
            controller.current = ctrl;
            setStatus("searching");
            const params = new URLSearchParams({ q: q.trim() });
            if (mode === "auth" && excludeLicenseNo) params.set("licenseNo", excludeLicenseNo);
            const url = mode === "public" ? `/partner-search?${params}` : `/supply-chain/license-search?${params}`;
            try {
                const res = await apiClient.get(url, { signal: ctrl.signal });
                setResults(res.data || []);
                setStatus("done");
            } catch (err) {
                if (err?.name === "CanceledError" || err?.name === "AbortError") return;
                setResults([]);
                setStatus("error");
            }
        },
        [mode, excludeLicenseNo]
    );

    const search = useCallback(
        (q) => {
            setQuery(q);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => run(q), DEBOUNCE_MS);
        },
        [run]
    );

    const reset = useCallback(() => {
        clearTimeout(timer.current);
        controller.current?.abort();
        setQuery("");
        setResults([]);
        setStatus("idle");
    }, []);

    return { query, results, status, search, reset, minQuery: MIN_QUERY };
}
